import React, {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
} from "react";
import { useResolvedTheme } from "@xsolla/xui-core";
import type { SvgThemedProps } from "./types";

const resolveToken = (value: any, theme: any) => {
  if (typeof value === "string" && value.startsWith("$")) {
    const token = value.slice(1);

    // Mapping for common tokens used in display packages
    const tokenMap: Record<string, any> = {
      colors_core_text_primary: theme.colors.content.primary,
      colors_core_text_secondary: theme.colors.content.secondary,
      colors_core_text_tertiary: theme.colors.content.tertiary,
      colors_core_text_brand: theme.colors.content.brand.primary,
      colors_core_text_success: theme.colors.content.success.primary,
      colors_core_text_warning: theme.colors.content.warning.primary,
      colors_core_text_alert: theme.colors.content.alert.primary,
      colors_core_text_neutral: theme.colors.content.neutral.primary,
      colors_control_faint_bg: theme.colors.control.mono.secondary.bg,
    };

    return tokenMap[token] || value;
  }
  return value;
};

const processChildren = (
  children: React.ReactNode,
  theme: any
): React.ReactNode => {
  return Children.map(children, (child) => {
    if (!isValidElement(child)) return child;

    const props: any = { ...(child.props as any) };
    const themeableProps = ["fill", "stroke", "stopColor", "color"];

    themeableProps.forEach((prop) => {
      if (props[prop]) {
        props[prop] = resolveToken(props[prop], theme);
      }
    });

    if (props.children) {
      props.children = processChildren(props.children, theme);
    }

    return cloneElement(child, props);
  });
};

export const SvgThemed = forwardRef<SVGSVGElement, SvgThemedProps>(
  (
    {
      children,
      themeMode,
      themeProductContext,
      testID,
      "data-testid": dataTestId,
      ...props
    },
    ref
  ) => {
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });
    const themedChildren = processChildren(children, theme);

    return (
      <svg ref={ref} data-testid={dataTestId || testID} {...props}>
        {themedChildren}
      </svg>
    );
  }
);

SvgThemed.displayName = "SvgThemed";
